/**
 * Design Tokens - Marine Sealink Website
 * Centralized design system for consistent styling across all components
 */

// Color Palette
export const COLORS = {
  // Brand Colors
  primary: {
    dark: '#082032',      // Marine navy - primary brand color
    teal: '#40a8c4',      // Marine teal - accent color
    lightTeal: '#5fb9cf', // Lighter teal for hover states
  },

  // Backgrounds
  background: {
    white: '#ffffff',
    light: '#f8fafc',
    lightBlue: '#f4faff',
    lightTeal: '#eaf6fa',
  },

  // Text Colors
  text: {
    primary: '#3a506b',   // Main text color
    dark: '#082032',      // Dark text (matches brand)
    light: '#6b7280',     // Light text for secondary content
    white: '#ffffff',
  },

  // UI Colors
  ui: {
    border: '#e5e7eb',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    whatsapp: '#25D366',
    whatsappHover: '#1da851',
    focus: '#40a8c4',
  },
};

// Typography
export const TYPOGRAPHY = {
  // Font Families
  fontFamily: {
    base: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
  },

  // Font Sizes (Mobile-first, scales up)
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    md: '1.125rem',   // 18px
    lg: '1.25rem',    // 20px
    xl: '1.5rem',     // 24px
    '2xl': '1.875rem', // 30px
    '3xl': '2.25rem',  // 36px
    '4xl': '3rem',     // 48px
  },

  // Font Weights
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line Heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Spacing Scale (using 0.25rem = 4px base)
export const SPACING = {
  xs: '0.5rem',   // 8px
  sm: '0.75rem',  // 12px
  md: '1rem',     // 16px
  lg: '1.5rem',   // 24px
  xl: '2rem',     // 32px
  '2xl': '2.5rem', // 40px
  '3xl': '3rem',   // 48px
  '4xl': '4rem',   // 64px
  '5xl': '5rem',   // 80px
  '6xl': '6rem',   // 96px
};

// Shadows (elevation system)
export const SHADOWS = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(8, 32, 50, 0.05)',
  sm: '0 2px 8px 0 rgba(8, 32, 50, 0.08)',
  md: '0 4px 16px 0 rgba(8, 32, 50, 0.10)',
  lg: '0 8px 24px 0 rgba(8, 32, 50, 0.12)',
  xl: '0 12px 32px 0 rgba(8, 32, 50, 0.15)',
  '2xl': '0 16px 48px 0 rgba(8, 32, 50, 0.18)',

  // Hover states (slightly elevated)
  hover: {
    sm: '0 4px 12px 0 rgba(8, 32, 50, 0.12)',
    md: '0 8px 20px 0 rgba(8, 32, 50, 0.15)',
    lg: '0 12px 28px 0 rgba(8, 32, 50, 0.18)',
  },
};

// Border Radius
export const RADIUS = {
  none: '0',
  sm: '0.25rem',  // 4px
  md: '0.5rem',   // 8px
  lg: '0.75rem',  // 12px
  xl: '1rem',     // 16px
  '2xl': '1.5rem', // 24px
  full: '9999px',
};

// Breakpoints (matching Bootstrap + custom)
export const BREAKPOINTS = {
  xs: '0px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1400px',
};

// Media Queries (mobile-first)
export const MEDIA = {
  sm: `@media (min-width: ${BREAKPOINTS.sm})`,
  md: `@media (min-width: ${BREAKPOINTS.md})`,
  lg: `@media (min-width: ${BREAKPOINTS.lg})`,
  xl: `@media (min-width: ${BREAKPOINTS.xl})`,
  xxl: `@media (min-width: ${BREAKPOINTS.xxl})`,

  // Max-width queries for mobile-specific styles
  maxSm: `@media (max-width: 575.98px)`,
  maxMd: `@media (max-width: 767.98px)`,
  maxLg: `@media (max-width: 991.98px)`,
  maxXl: `@media (max-width: 1199.98px)`,
};

// Transitions
export const TRANSITIONS = {
  fast: '150ms ease-in-out',
  base: '200ms ease-in-out',
  slow: '300ms ease-in-out',
  slower: '500ms ease-in-out',

  // Specific properties
  all: 'all 300ms ease-in-out',
  colors: 'color 200ms ease-in-out, background-color 200ms ease-in-out, border-color 200ms ease-in-out',
  transform: 'transform 300ms ease-in-out',
  opacity: 'opacity 200ms ease-in-out',
  shadow: 'box-shadow 300ms ease-in-out',
};

// Z-Index Scale
export const Z_INDEX = {
  base: 1,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// Gradients
export const GRADIENTS = {
  primary: `linear-gradient(135deg, ${COLORS.primary.dark} 0%, ${COLORS.primary.teal} 100%)`,
  primaryHover: `linear-gradient(135deg, ${COLORS.primary.dark} 0%, ${COLORS.primary.lightTeal} 100%)`,
  teal: `linear-gradient(135deg, ${COLORS.primary.teal} 0%, ${COLORS.primary.lightTeal} 100%)`,
  overlay: `linear-gradient(135deg, rgba(8, 32, 50, 0.85) 0%, rgba(64, 168, 196, 0.85) 100%)`,
  overlayLight: `linear-gradient(135deg, rgba(8, 32, 50, 0.70) 0%, rgba(64, 168, 196, 0.70) 100%)`,

  // Animated gradients (multi-stop for richer animation)
  animated: `linear-gradient(-45deg, ${COLORS.primary.dark}, ${COLORS.primary.teal}, #2c5f77, ${COLORS.primary.dark})`,
  animatedHero: `linear-gradient(-45deg, ${COLORS.primary.dark} 0%, #1a3a4f 25%, ${COLORS.primary.teal} 50%, #2c5f77 75%, ${COLORS.primary.dark} 100%)`,

  // Glassmorphism backgrounds
  glass: `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)`,
  glassLight: `linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)`,
  glassDark: `linear-gradient(135deg, rgba(8, 32, 50, 0.7) 0%, rgba(8, 32, 50, 0.5) 100%)`,
};

// Button Styles (reusable button configurations)
export const BUTTON_STYLES = {
  primary: {
    background: GRADIENTS.primary,
    color: COLORS.text.white,
    padding: `${SPACING.md} ${SPACING.xl}`,
    borderRadius: RADIUS.lg,
    border: 'none',
    fontWeight: TYPOGRAPHY.fontWeight.semibold,
    transition: TRANSITIONS.all,
    boxShadow: SHADOWS.md,
  },

  secondary: {
    background: 'transparent',
    color: COLORS.primary.teal,
    padding: `${SPACING.md} ${SPACING.xl}`,
    borderRadius: RADIUS.lg,
    border: `2px solid ${COLORS.primary.teal}`,
    fontWeight: TYPOGRAPHY.fontWeight.semibold,
    transition: TRANSITIONS.all,
  },

  whatsapp: {
    background: COLORS.ui.whatsapp,
    color: COLORS.text.white,
    padding: `${SPACING.md} ${SPACING.xl}`,
    borderRadius: RADIUS.lg,
    border: 'none',
    fontWeight: TYPOGRAPHY.fontWeight.semibold,
    transition: TRANSITIONS.all,
    boxShadow: SHADOWS.md,
  },
};

// Card Styles (reusable card configurations)
export const CARD_STYLES = {
  base: {
    background: COLORS.background.white,
    borderRadius: RADIUS.xl,
    padding: SPACING['2xl'],
    boxShadow: SHADOWS.md,
    transition: TRANSITIONS.all,
    border: `1px solid ${COLORS.ui.border}`,
  },

  hover: {
    boxShadow: SHADOWS.hover.lg,
    transform: 'translateY(-4px)',
  },
};

// Glassmorphism Effects
export const GLASSMORPHISM = {
  // Standard glass effect (light)
  light: {
    background: GRADIENTS.glassLight,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)', // Safari support
    border: `1px solid rgba(255, 255, 255, 0.18)`,
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  },

  // Medium glass effect
  medium: {
    background: GRADIENTS.glass,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid rgba(255, 255, 255, 0.12)`,
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
  },

  // Strong glass effect (more blur)
  strong: {
    background: GRADIENTS.glassDark,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: `1px solid rgba(255, 255, 255, 0.08)`,
    boxShadow: '0 8px 32px 0 rgba(8, 32, 50, 0.3)',
  },

  // Navbar glass (when scrolled)
  navbar: {
    background: 'rgba(8, 32, 50, 0.85)',
    backdropFilter: 'blur(12px) saturate(180%)',
    WebkitBackdropFilter: 'blur(12px) saturate(180%)',
    borderBottom: `1px solid rgba(64, 168, 196, 0.2)`,
    boxShadow: '0 4px 24px 0 rgba(8, 32, 50, 0.2)',
  },
};

// Animation Keyframes (CSS-in-JS compatible)
export const ANIMATIONS = {
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },

  slideUp: {
    from: { transform: 'translateY(20px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
  },

  scaleIn: {
    from: { transform: 'scale(0.95)', opacity: 0 },
    to: { transform: 'scale(1)', opacity: 1 },
  },

  // Animated gradient keyframes
  gradientShift: `
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `,

  // Pulse glow effect
  pulseGlow: `
    0%, 100% { box-shadow: 0 0 20px rgba(64, 168, 196, 0.3); }
    50% { box-shadow: 0 0 30px rgba(64, 168, 196, 0.6); }
  `,
};

// Hero Section Heights
export const HERO_HEIGHTS = {
  default: '70vh',
  page: '60vh',
  minHeight: '500px',
  maxHeight: '900px',
};

// Container Max Widths (if needed for custom layouts)
export const CONTAINER = {
  sm: '540px',
  md: '720px',
  lg: '960px',
  xl: '1140px',
  xxl: '1320px',
};

const designTokens = {
  COLORS,
  TYPOGRAPHY,
  SPACING,
  SHADOWS,
  RADIUS,
  BREAKPOINTS,
  MEDIA,
  TRANSITIONS,
  Z_INDEX,
  GRADIENTS,
  GLASSMORPHISM,
  BUTTON_STYLES,
  CARD_STYLES,
  ANIMATIONS,
  HERO_HEIGHTS,
  CONTAINER,
};

export default designTokens;
